import { X, MapPin, Calendar, DollarSign, User, Mail, Phone, Briefcase } from 'lucide-react';

interface Lead {
  id: string;
  title: string;
  category: string;
  tradesmens_commission: string;
  description: string;
  budget: number;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  lead_sub_cat: string;
  postedDate: string;
  status: string;
  homeowner: {
    id: number;
    name: string;
    email: string;
    phone: string;
    memberSince: string;
    completedProjects: number;
  };
  images: string[];
  media: Array<{
    type: string;
    url: string;
  }>;
  activities?: Array<{
    id: string;
    type: 'status_change' | 'assigned' | 'note' | 'created';
    description: string;
    timestamp: string;
    user: string;
  }>;
}

interface LeadDetailsModalProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
}

export function LeadDetailsModal({ lead, isOpen, onClose }: LeadDetailsModalProps) {
  if (!isOpen || !lead) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'created':
        return '🎯';
      case 'status_change':
        return '🔄';
      case 'assigned':
        return '👷';
      case 'note':
        return '📝';
      default:
        return '📌';
    }
  };

  return (
    <div style={{
      zIndex:9999
    }} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 lead-details-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white  border-b px-6 py-1 flex items-center justify-between z-10">
          <div>
            <div className="text-2xl font-[500] text-gray-900">Lead #{lead.id}</div>
            <div className="text-sm text-gray-500">{lead.title}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-transparent border-0 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  lead.status === 'declined' ? 'bg-red-100 text-red-700' :
                  lead.status === 'active' ? 'bg-green-100 text-green-700' :
                  lead.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                </span>
                <span className="text-sm text-gray-500">Category: {lead.category}</span>
              </div>

              {/* Images */}
              {lead.media && lead.media.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Project Images</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {lead.media.map((item, index) => (
                      item.type === 'image' && (
                        <div key={index} className="relative rounded-lg overflow-hidden bg-gray-100 aspect-video">
                          <img
                            src={item.url}
                            alt={`Lead image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400';
                            }}
                          />
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                  <Briefcase className="w-4 h-4 text-gray-500" />
                  Description
                </h3>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{lead.description}</div>
                <div className="text-xs text-gray-500 mt-2">Subcategory: {lead.lead_sub_cat}</div>
              </div>

              {/* Budget & Commission */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 text-blue-700 mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">Budget</span>
                  </div>
                  <div className="text-2xl font-medium text-blue-900">${lead.budget.toLocaleString()}</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">Commission</span>
                  </div>
                  <p className="text-2xl font-medium text-green-900">${lead.tradesmens_commission}</p>
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Location
                </h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">{lead.location.address}</p>
                  <p className="text-sm text-gray-600">
                    {lead.location.city}, {lead.location.state} {lead.location.zipCode}
                  </p>
                </div>
              </div>

              {/* Posted Date */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  Posted Date
                </h3>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{formatDate(lead.postedDate)}</p>
              </div>

              {/* Homeowner Information */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-3">
                  <User className="w-4 h-4 text-gray-500" />
                  Homeowner Information
                </h3>
                <div className="bg-gray-50 p-3 rounded-lg space-y-3 border border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="text-sm font-medium text-gray-900">{lead.homeowner.name}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Email
                      </p>
                      <a href={`mailto:${lead.homeowner.email}`} className="text-sm text-blue-600 hover:underline">
                        {lead.homeowner.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Phone
                      </p>
                      <a href={`tel:${lead.homeowner.phone}`} className="text-sm text-blue-600 hover:underline">
                        {lead.homeowner.phone}
                      </a>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Member Since</p>
                      <p className="text-sm text-gray-900">{formatDate(lead.homeowner.memberSince)}</p>
                    </div>
                    {/* <div>
                      <p className="text-xs text-gray-500">Completed Projects</p>
                      <p className="text-sm text-gray-900">{lead.homeowner.completedProjects}</p>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Activity Timeline */}
            <div className="lg:col-span-1">
              <div className="sticky top-20">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Activity Timeline</h3>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 max-h-[600px] overflow-y-auto">
                  {lead.activities && lead.activities.length > 0 ? (
                    <div className="space-y-4">
                      {[...lead.activities].reverse().map((activity, index) => (
                        <div key={activity.id} className="relative">
                          {index !== lead.activities!.length - 1 && (
                            <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-300"></div>
                          )}
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center border-2 border-gray-300 text-sm">
                              {getActivityIcon(activity.type)}
                            </div>
                            <div className="flex-1 pb-3">
                              <div className="text-xs text-gray-900 font-medium">{activity.description}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDateTime(activity.timestamp)}
                              </div>
                              <div className="text-xs text-gray-400">by {activity.user}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">No activity yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 border-0 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
